import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { FiDatabase, FiSearch, FiRefreshCw, FiTrash2, FiEye, FiDownload, FiFilter } from 'react-icons/fi';
import { Modal, Button } from 'react-bootstrap';

export default function DataExplorer() {
  const [collections, setCollections] = useState([
    'products',
    'categories',
    'customers',
    'employees',
    'roles',
    'permissions',
    'orders',
    'invoices',
    'terminals',
    'terminal_settings',
    'sales',
    'stock_transactions',
    'payments',
    'discounts',
    'taxes',
    'vendors',
    'purchase_orders',
    'expenses',
    'reports',
    'settings',
    'audit_logs',
    'notifications'
  ]);
  const [selectedCollection, setSelectedCollection] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const { selectedHierarchy } = useAuth();

  useEffect(() => {
    if (selectedCollection && selectedHierarchy?.outlet?.id) {
      loadCollectionData();
    }
  }, [selectedCollection, selectedHierarchy]);

  const loadCollectionData = async () => {
    if (!selectedCollection || !selectedHierarchy?.outlet?.id) {
      return;
    }

    try {
      setLoading(true);
      setData([]);

      // Query collection with outletId filter
      const collectionRef = collection(db, selectedCollection);
      const q = query(collectionRef, where('outletId', '==', selectedHierarchy.outlet.id));
      const snapshot = await getDocs(q);
      
      const documents = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setData(documents);
    } catch (error) {
      console.error('Error loading collection data:', error);
      alert('Failed to load data. Some collections might not have outletId field.');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (selectedCollection) {
      loadCollectionData();
    }
  };

  const handleCollectionChange = (collectionName) => {
    setSelectedCollection(collectionName);
    setSearchTerm('');
    setColumnFilters({});
    setShowFilters(false);
  };

  const handleColumnFilterChange = (column, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setColumnFilters({});
  };

  const clearColumnFilter = (column) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[column];
      return newFilters;
    });
  };

  const handleViewDocument = (document) => {
    setSelectedDocument(document);
    setShowViewModal(true);
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, selectedCollection, documentId));
      alert('Document deleted successfully!');
      loadCollectionData();
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document.');
    }
  };

  const handleDeleteAll = async () => {
    const confirmMessage = filteredData.length === data.length
      ? `Are you sure you want to delete ALL ${data.length} documents from "${selectedCollection}"?\n\nThis action cannot be undone!`
      : `Are you sure you want to delete the ${filteredData.length} FILTERED documents?\n\n(Total documents: ${data.length})\n\nThis action cannot be undone!`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Second confirmation for safety
    const finalConfirm = window.prompt(
      `Type "DELETE ALL" to confirm deletion of ${filteredData.length} documents:`
    );

    if (finalConfirm !== 'DELETE ALL') {
      alert('Deletion cancelled. Text did not match.');
      return;
    }

    try {
      setLoading(true);
      let successCount = 0;
      let errorCount = 0;

      // Delete documents in batches
      for (const document of filteredData) {
        try {
          await deleteDoc(doc(db, selectedCollection, document.id));
          successCount++;
        } catch (error) {
          console.error(`Error deleting document ${document.id}:`, error);
          errorCount++;
        }
      }

      alert(`Deletion complete!\n\nDeleted: ${successCount}\nFailed: ${errorCount}`);
      loadCollectionData();
    } catch (error) {
      console.error('Error during bulk delete:', error);
      alert('Failed to complete bulk deletion.');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (filteredData.length === 0) {
      alert('No data to export');
      return;
    }

    const dataStr = JSON.stringify(filteredData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedCollection}_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const filteredData = data.filter(doc => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = Object.entries(doc).some(([key, value]) => {
        if (key === 'id') return value.toLowerCase().includes(searchLower);
        if (typeof value === 'string') return value.toLowerCase().includes(searchLower);
        if (typeof value === 'number') return value.toString().includes(searchLower);
        if (typeof value === 'boolean') return value.toString().includes(searchLower);
        return false;
      });
      if (!matchesSearch) return false;
    }
    
    // Column filters
    if (Object.keys(columnFilters).length > 0) {
      return Object.entries(columnFilters).every(([column, filterValue]) => {
        if (!filterValue) return true;
        
        const docValue = doc[column];
        const filterLower = filterValue.toLowerCase();
        
        if (docValue === null || docValue === undefined) return false;
        if (typeof docValue === 'string') return docValue.toLowerCase().includes(filterLower);
        if (typeof docValue === 'number') return docValue.toString().includes(filterLower);
        if (typeof docValue === 'boolean') return docValue.toString().includes(filterLower);
        if (typeof docValue === 'object') {
          if (docValue.toDate || docValue.seconds) {
            return getDisplayValue(docValue).toLowerCase().includes(filterLower);
          }
          return JSON.stringify(docValue).toLowerCase().includes(filterLower);
        }
        return false;
      });
    }
    
    return true;
  });

  const getDisplayValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') {
      if (value.toDate && typeof value.toDate === 'function') {
        // Firestore Timestamp
        return new Date(value.toDate()).toLocaleString();
      }
      if (value.seconds) {
        // Firestore Timestamp object
        return new Date(value.seconds * 1000).toLocaleString();
      }
      return JSON.stringify(value);
    }
    return value.toString();
  };

  const getTableColumns = () => {
    if (filteredData.length === 0) return [];
    
    // Get all unique keys from all documents
    const allKeys = new Set();
    filteredData.forEach(doc => {
      Object.keys(doc).forEach(key => allKeys.add(key));
    });
    
    // Prioritize common fields
    const priorityFields = ['id', 'name', 'code', 'title', 'email', 'phone', 'status', 'isActive', 'createdAt', 'updatedAt'];
    const sortedKeys = Array.from(allKeys).sort((a, b) => {
      const aIndex = priorityFields.indexOf(a);
      const bIndex = priorityFields.indexOf(b);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    });

    return sortedKeys;
  };

  const tableColumns = getTableColumns();

  return (
    <div className="container-fluid">
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 className="page-title">
              <FiDatabase className="me-2" />
              Data Explorer
            </h1>
            <p className="page-subtitle">
              Browse and explore collection data
              {selectedHierarchy?.outlet?.name && (
                <span className="text-primary ms-2">
                  • {selectedHierarchy.outlet.name}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* No Outlet Selected Warning */}
      {!selectedHierarchy?.outlet?.id ? (
        <div className="alert alert-warning d-flex align-items-center" role="alert">
          <FiDatabase className="me-2" size={24} />
          <div>
            <strong>No Outlet Selected</strong>
            <p className="mb-0">Please select an outlet to explore data.</p>
          </div>
        </div>
      ) : (
        <>
          {/* Collection Selector */}
          <div className="card hover-lift mb-4">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-6">
                  <label className="form-label">
                    <FiFilter className="me-2" />
                    Select Collection
                  </label>
                  <select
                    className="form-select form-select-lg"
                    value={selectedCollection}
                    onChange={(e) => handleCollectionChange(e.target.value)}
                  >
                    <option value="">Choose a collection...</option>
                    {collections.map(col => (
                      <option key={col} value={col}>
                        {col.charAt(0).toUpperCase() + col.slice(1).replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedCollection && (
                  <>
                    <div className="col-md-4">
                      <label className="form-label">
                        <FiSearch className="me-2" />
                        Global Search
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search in all fields..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2">
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => setShowFilters(!showFilters)}
                          title="Column Filters"
                        >
                          <FiFilter />
                        </button>
                        <button
                          className="btn btn-outline-primary"
                          onClick={handleRefresh}
                          disabled={loading}
                          title="Refresh"
                        >
                          <FiRefreshCw className={loading ? 'spin' : ''} />
                        </button>
                        <button
                          className="btn btn-outline-success"
                          onClick={handleExport}
                          disabled={filteredData.length === 0}
                          title="Export to JSON"
                        >
                          <FiDownload />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Active Filters Summary */}
              {selectedCollection && (searchTerm || Object.keys(columnFilters).length > 0) && (
                <div className="mt-3">
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <small className="text-muted">Active Filters:</small>
                    {searchTerm && (
                      <span className="badge bg-primary">
                        Global: {searchTerm}
                        <button
                          className="btn-close btn-close-white ms-2"
                          style={{ fontSize: '0.6rem' }}
                          onClick={() => setSearchTerm('')}
                        ></button>
                      </span>
                    )}
                    {Object.entries(columnFilters).map(([column, value]) => value && (
                      <span key={column} className="badge bg-info">
                        {column}: {value}
                        <button
                          className="btn-close btn-close-white ms-2"
                          style={{ fontSize: '0.6rem' }}
                          onClick={() => clearColumnFilter(column)}
                        ></button>
                      </span>
                    ))}
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={clearAllFilters}
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Column Filters Panel */}
          {showFilters && selectedCollection && data.length > 0 && (
            <div className="card hover-lift mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  <FiFilter className="me-2" />
                  Column Filters
                </h6>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowFilters(false)}
                >
                  Hide Filters
                </button>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  {tableColumns.map(column => (
                    <div key={column} className="col-md-3 col-sm-4 col-6">
                      <label className="form-label small">
                        {column.charAt(0).toUpperCase() + column.slice(1).replace(/([A-Z])/g, ' $1').trim()}
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={`Filter ${column}...`}
                        value={columnFilters[column] || ''}
                        onChange={(e) => handleColumnFilterChange(column, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Stats */}
          {selectedCollection && !loading && data.length > 0 && (
            <div className="row g-4 mb-4">
              <div className="col-12 col-sm-6 col-lg-3">
                <div className="card stat-card hover-lift">
                  <div className="card-body">
                    <div className="stat-card-icon" style={{ background: '#ff9800' }}>
                      <FiDatabase className="text-white" />
                    </div>
                    <div className="stat-card-value">{selectedCollection}</div>
                    <div className="stat-card-label">Collection</div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-sm-6 col-lg-3">
                <div className="card stat-card hover-lift">
                  <div className="card-body">
                    <div className="stat-card-icon" style={{ background: '#2196F3' }}>
                      <FiDatabase className="text-white" />
                    </div>
                    <div className="stat-card-value">{data.length}</div>
                    <div className="stat-card-label">Total Documents</div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-sm-6 col-lg-3">
                <div className="card stat-card hover-lift">
                  <div className="card-body">
                    <div className="stat-card-icon" style={{ background: '#48bb78' }}>
                      <FiSearch className="text-white" />
                    </div>
                    <div className="stat-card-value">{filteredData.length}</div>
                    <div className="stat-card-label">Filtered Results</div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-sm-6 col-lg-3">
                <div className="card stat-card hover-lift">
                  <div className="card-body">
                    <div className="stat-card-icon" style={{ background: '#9c27b0' }}>
                      <FiFilter className="text-white" />
                    </div>
                    <div className="stat-card-value">{tableColumns.length}</div>
                    <div className="stat-card-label">Fields</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Data Table */}
          {selectedCollection && (
            <div className="card hover-lift">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  {selectedCollection.charAt(0).toUpperCase() + selectedCollection.slice(1).replace(/_/g, ' ')} Data
                </h5>
                <div className="d-flex gap-2 align-items-center">
                  {filteredData.length > 0 && (
                    <>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={handleDeleteAll}
                        disabled={loading}
                      >
                        <FiTrash2 className="me-1" size={14} />
                        Delete {filteredData.length === data.length ? 'All' : 'Filtered'} ({filteredData.length})
                      </button>
                      <span className="badge bg-primary">
                        {filteredData.length} record{filteredData.length !== 1 ? 's' : ''}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="card-body">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-2">Loading data...</p>
                  </div>
                ) : filteredData.length === 0 ? (
                  <div className="text-center py-5">
                    <FiDatabase size={48} className="text-muted mb-3" />
                    <p className="text-muted mb-0">
                      {searchTerm ? 'No documents match your search.' : 'No documents found in this collection.'}
                    </p>
                    {searchTerm && (
                      <button
                        className="btn btn-sm btn-outline-primary mt-2"
                        onClick={() => setSearchTerm('')}
                      >
                        Clear Search
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle table-sm">
                      <thead className="sticky-top">
                        <tr>
                          <th 
                            style={{ 
                              minWidth: '50px', 
                              position: 'sticky', 
                              left: 0, 
                              zIndex: 10
                            }}
                          >
                            #
                          </th>
                          {tableColumns.map(column => (
                            <th key={column} style={{ minWidth: '150px', whiteSpace: 'nowrap' }}>
                              {column.charAt(0).toUpperCase() + column.slice(1).replace(/([A-Z])/g, ' $1').trim()}
                            </th>
                          ))}
                          <th 
                            className="text-end" 
                            style={{ 
                              minWidth: '120px', 
                              position: 'sticky', 
                              right: 0, 
                              zIndex: 10
                            }}
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.map((doc, index) => (
                          <tr key={doc.id}>
                            <td 
                              className="text-muted sticky-column-left" 
                              style={{ 
                                position: 'sticky', 
                                left: 0, 
                                zIndex: 5
                              }}
                            >
                              {index + 1}
                            </td>
                            {tableColumns.map(column => (
                              <td key={column}>
                                {column === 'id' ? (
                                  <code className="text-primary small" title={doc[column]}>{doc[column]?.substring(0, 12)}...</code>
                                ) : column === 'isActive' || column === 'status' ? (
                                  <span className={`badge ${doc[column] === true || doc[column] === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                                    {getDisplayValue(doc[column])}
                                  </span>
                                ) : typeof doc[column] === 'boolean' ? (
                                  <span className={`badge ${doc[column] ? 'bg-success' : 'bg-secondary'}`}>
                                    {doc[column] ? 'Yes' : 'No'}
                                  </span>
                                ) : typeof doc[column] === 'object' && doc[column] !== null ? (
                                  doc[column].toDate || doc[column].seconds ? (
                                    <span className="text-muted small" title={getDisplayValue(doc[column])}>
                                      {getDisplayValue(doc[column])?.toString().substring(0, 30)}
                                      {getDisplayValue(doc[column])?.toString().length > 30 ? '...' : ''}
                                    </span>
                                  ) : (
                                    <span className="text-muted small font-monospace" title={JSON.stringify(doc[column])}>
                                      {Array.isArray(doc[column]) ? `[${doc[column].length} items]` : '{Object}'}
                                    </span>
                                  )
                                ) : (
                                  <span title={getDisplayValue(doc[column])?.toString()}>
                                    {getDisplayValue(doc[column])?.toString().substring(0, 40)}
                                    {getDisplayValue(doc[column])?.toString().length > 40 ? '...' : ''}
                                  </span>
                                )}
                              </td>
                            ))}
                            <td 
                              className="sticky-column-right"
                              style={{ 
                                position: 'sticky', 
                                right: 0, 
                                zIndex: 5
                              }}
                            >
                              <div className="d-flex gap-2 justify-content-end">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handleViewDocument(doc)}
                                  title="View Details"
                                >
                                  <FiEye size={14} />
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDelete(doc.id)}
                                  title="Delete"
                                >
                                  <FiTrash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* View Document Modal */}
      <Modal 
        show={showViewModal} 
        onHide={() => { setShowViewModal(false); setSelectedDocument(null); }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FiEye className="me-2" />
            Document Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDocument && (
            <div>
              <div className="mb-3">
                <strong>Document ID:</strong>
                <br />
                <code className="text-primary">{selectedDocument.id}</code>
              </div>
              <hr />
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <table className="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th style={{ width: '30%' }}>Field</th>
                      <th style={{ width: '20%' }}>Type</th>
                      <th style={{ width: '50%' }}>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(selectedDocument)
                      .filter(([key]) => key !== 'id')
                      .map(([key, value]) => (
                        <tr key={key}>
                          <td>
                            <code className="text-secondary">{key}</code>
                          </td>
                          <td>
                            <span className="badge bg-info">
                              {typeof value === 'object' && value !== null
                                ? value.toDate
                                  ? 'Timestamp'
                                  : Array.isArray(value)
                                  ? 'Array'
                                  : 'Object'
                                : typeof value}
                            </span>
                          </td>
                          <td>
                            <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {typeof value === 'object' && value !== null
                                ? JSON.stringify(value, null, 2)
                                : getDisplayValue(value)}
                            </pre>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => { setShowViewModal(false); setSelectedDocument(null); }}
          >
            Close
          </Button>
          <Button 
            variant="primary"
            onClick={() => {
              const dataStr = JSON.stringify(selectedDocument, null, 2);
              navigator.clipboard.writeText(dataStr);
              alert('Document copied to clipboard!');
            }}
          >
            Copy JSON
          </Button>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        /* Sticky column background - inherit from table row */
        .sticky-column-left,
        .sticky-column-right {
          background-color: inherit !important;
        }
        
        /* Ensure sticky columns match row background on hover */
        .table-hover tbody tr:hover .sticky-column-left,
        .table-hover tbody tr:hover .sticky-column-right {
          background-color: inherit !important;
        }
        
        /* Sticky header columns */
        thead th {
          background-color: var(--bs-table-bg) !important;
        }
        
        /* Dark theme specific fixes */
        [data-bs-theme="dark"] thead th,
        .dark thead th {
          background-color: var(--bs-table-bg) !important;
        }
        
        [data-bs-theme="dark"] .sticky-column-left,
        [data-bs-theme="dark"] .sticky-column-right,
        .dark .sticky-column-left,
        .dark .sticky-column-right {
          background-color: inherit !important;
        }
      `}</style>
    </div>
  );
}

